"use client";

import { type InputHTMLAttributes, useRef, type FC, type Ref } from "react";
import { mergeClasses } from "@huub-web/utils";
import type { RecipeVariants } from "@vanilla-extract/recipes";

import { Icon } from "../Icon";
import { Flex } from "../Flex";
import type { ActiveProp, ClassNameProp, ValidatableProps } from "../../props";
import { Text } from "../Text";
import { useFormValidation } from "../../hooks/useFormValidation";

import { mergeRefs } from "../../utils";

import {
  checkboxStyle,
  checkboxInputStyle,
  checkStyle,
  checkboxWrapperStyle,
  labelStyle,
} from "./Checkbox.css";
import { focusWithin } from "../../theme/css/focusVisible.css";

export type CheckboxChangeHandler = (checked: boolean) => void;

export type CheckboxProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "onChange" | "size" | "value" | "checked"
> &
  RecipeVariants<typeof checkboxStyle> &
  ValidatableProps &
  ActiveProp &
  ClassNameProp & {
    label?: string;
    checked?: boolean;
    highlight?: boolean;
    success?: boolean;
    onChange?: CheckboxChangeHandler;
    /**
     * Ref forwarded to the input element
     */
    ref?: Ref<HTMLInputElement>;
  };

/**
 * Checkbox component.
 */
export const Checkbox: FC<CheckboxProps> = ({
  id: idProp,
  disabled: disabledProp,
  error: errorProp,
  validation,
  name,
  className,
  label,
  size = "m",
  highlight,
  success,
  defaultChecked,
  checked,
  active,
  onChange,
  ref,
  ...props
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const { id, error, disabled } = useFormValidation(checked, {
    id: idProp,
    error: errorProp,
    disabled: disabledProp,
    validation,
    name,
    ref: inputRef,
  });

  return (
    <Flex className={mergeClasses(checkboxWrapperStyle, className)}>
      <label
        htmlFor={id}
        className={mergeClasses(
          checkboxStyle({
            checked,
            size,
            disabled,
            error,
            success,
            highlight,
          }),
          focusWithin,
        )}
        {...(disabled && { "aria-disabled": true })}
        {...(checked && { "aria-checked": true })}
        {...(active && { "data-active": true })}
      >
        <input
          ref={mergeRefs(ref, inputRef)}
          id={id}
          type="checkbox"
          checked={checked}
          className={checkboxInputStyle}
          defaultChecked={defaultChecked}
          onChange={() => onChange?.(!checked)}
          disabled={disabled}
          {...props}
        />
        <Icon
          icon="checkThick"
          className={checkStyle({ checked })}
          size={size === "s" ? "xs" : "s"}
        />
      </label>

      {/* <Reveal>
          {success && (
            <Badge
              variant="success"
              size="xs"
              value={<Icon icon="checkThick" size="xs" />}
              offset="large"
            />
          )}
        </Reveal> */}

      {label && (
        <Text as="label" htmlFor={id} className={labelStyle({ disabled })}>
          {label}
        </Text>
      )}
    </Flex>
  );
};
